'use client'

import {
    createContext,
    Fragment,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from 'react'

import { Container } from '@cleartrip/ct-design-components'

import RecentSearchWidgetWrapper from '@/components/home/RecentSearchWidgetWrapper'
import UnlockEliteBanner from '@/components/home/UnlockEliteBanner'
import { getUnifiedWidgetsDataFromApi } from '@/api/homepage/unifiedWidgetStack'
import {
    AIR_FUNNEL_JOURNEY_TYPE,
    AIR_LOYALTY_USECASE,
    CYS_MAX_CARDS,
    WIDGET_MARGIN_HEIGHT,
    WIDGET_STACK_GAP_PX
} from '@/constants/unifiedWidgetStack.constants'
import { deriveBannerVariant } from '@/utils/homepage/elite'
import { getLoyaltyRavenAttributes } from '@/utils/homepage/loyaltyAnalytics'
import { ravenSDKTrigger } from '@/analytics/raven/ravenSDKHelper'
import { LOYALTY_ACTION_NAME, LOYALTY_RAVEN_EVENT } from '@/constants/components/loyaltyAnalytics'
import { setLoyaltyLoginContext } from '@/utils/loyalty/loyaltyLoginContext'
import {
    combinedHeightFromUnifiedState,
    getUnifiedWidgetItemMappedForRender,
    getUnifiedWidgetsOnlyUsecasesString,
    getUpdatedWidgetState,
    hasContentItems,
    isAnyUnifiedHomeWidgetEnabled,
    normalizeUnifiedApiResponse
} from '@/utils/unifiedWidgetStack'
import type {
    ILoyaltyUhpContentItem,
    IUnifiedWidgetConfig,
    IUnifiedWidgetItem,
    IUnifiedWidgetState,
    IUnifiedWidgetStateUpdate
} from '@/types/unifiedExpandWidgets'

const FHP_PAGE_NAME = 'flights_home'

type IUnifiedWidgetStackContextValue = {
    unifiedState: IUnifiedWidgetState
    updateUnifiedWidgetState: (update: IUnifiedWidgetStateUpdate) => void
    loyalty: ILoyaltyUhpContentItem | null
    isSignedIn: boolean
}

const UnifiedWidgetStackContext = createContext<IUnifiedWidgetStackContextValue | null>(null)

export const useUnifiedWidgetStack = (): IUnifiedWidgetStackContextValue => {
    const ctx = useContext(UnifiedWidgetStackContext)
    if (!ctx) {
        throw new Error('useUnifiedWidgetStack must be used within UnifiedWidgetStackProvider')
    }
    return ctx
}

type ProviderProps = {
    children: ReactNode
    config: IUnifiedWidgetConfig
    isSignedIn: boolean
}

export const UnifiedWidgetStackProvider = ({ children, config, isSignedIn }: ProviderProps) => {
    const [unifiedState, setUnifiedState] = useState<IUnifiedWidgetState>([])

    const usecasesParam = useMemo(() => getUnifiedWidgetsOnlyUsecasesString(config), [config])

    const updateUnifiedWidgetState = useCallback(
        (update: IUnifiedWidgetStateUpdate) => setUnifiedState(prev => getUpdatedWidgetState(prev, update)),
        []
    )

    useEffect(() => {
        if (!isAnyUnifiedHomeWidgetEnabled(config) || !usecasesParam) {
            setUnifiedState([])
            return
        }
        let active = true
        const fetchData = async () => {
            const response = await getUnifiedWidgetsDataFromApi({ usecases: usecasesParam, size: CYS_MAX_CARDS })
            if (active) {
                setUnifiedState(normalizeUnifiedApiResponse(response))
            }
        }
        fetchData()
        return () => {
            active = false
        }
    }, [usecasesParam, isSignedIn, config])

    const loyalty = useMemo(() => {
        const item = unifiedState.find(u => u.usecase === AIR_LOYALTY_USECASE)
        const mapped = item ? getUnifiedWidgetItemMappedForRender(item) : null
        return mapped?.usecase === AIR_LOYALTY_USECASE ? mapped.loyalty : null
    }, [unifiedState])

    const value = useMemo<IUnifiedWidgetStackContextValue>(
        () => ({ unifiedState, updateUnifiedWidgetState, loyalty, isSignedIn }),
        [unifiedState, updateUnifiedWidgetState, loyalty, isSignedIn]
    )

    return <UnifiedWidgetStackContext.Provider value={value}>{children}</UnifiedWidgetStackContext.Provider>
}

type UnifiedLoyaltyBannerSlotProps = {
    onLoginClick: () => void
    onArrowClick?: () => void
}

export const UnifiedLoyaltyBannerSlot = memo(function UnifiedLoyaltyBannerSlot({
    onLoginClick,
    onArrowClick
}: UnifiedLoyaltyBannerSlotProps) {
    const { loyalty, isSignedIn } = useUnifiedWidgetStack()

    // Wait for the UHP loyalty row. No guest fallback: missing row / SUSPENDED => hide.
    const variant = loyalty
        ? deriveBannerVariant(loyalty.metaData?.loyaltyStatus, isSignedIn, loyalty.metaData?.displayStatus)
        : null

    const buildLoyaltyPayload = () => ({
        ...getLoyaltyRavenAttributes(loyalty?.metaData),
        page_name: FHP_PAGE_NAME
    })

    const impressionFiredRef = useRef(false)
    useEffect(() => {
        if (impressionFiredRef.current || !variant) {
            return
        }
        impressionFiredRef.current = true
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.WIDGET_IMPRESSION, buildLoyaltyPayload())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, loyalty])

    const handleLoginClick = () => {
        const payload = buildLoyaltyPayload()
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...payload,
            action_name: LOYALTY_ACTION_NAME.LOGGED_OUT_WIDGET_CLICK
        })
        // pass loyalty context to the login modal
        setLoyaltyLoginContext(payload)
        onLoginClick()
    }

    const handleBenefitsPopupLoad = () =>
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.BENEFITS_BOTTOMSHEET_LOAD, buildLoyaltyPayload())

    const handleViewBenefitsClick = () =>
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.BENEFITS_VIEW_CLICK
        })

    const handleBenefitsPopupDismiss = () =>
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.BENEFITS_DISMISS_CLICK
        })

    if (!variant) {
        return null
    }

    const handleArrowClick = () => {
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.LOGGED_IN_WIDGET_CLICK
        })
        onArrowClick?.()
    }

    return (
        <Container className="w-100p">
            <UnlockEliteBanner
                variant={variant}
                headlineText={loyalty?.title}
                stripText={loyalty?.subtitle}
                onLoginClick={handleLoginClick}
                onArrowClick={handleArrowClick}
                enableUnlockSoonModal
                onBenefitsPopupLoad={handleBenefitsPopupLoad}
                onViewBenefitsClick={handleViewBenefitsClick}
                onBenefitsPopupDismiss={handleBenefitsPopupDismiss}
            />
        </Container>
    )
})

type UnifiedRecentSearchSlotProps = {
    abVariant?: string
}

export const UnifiedRecentSearchSlot = memo(function UnifiedRecentSearchSlot({
    abVariant
}: UnifiedRecentSearchSlotProps) {
    const { unifiedState, updateUnifiedWidgetState } = useUnifiedWidgetStack()

    const item = unifiedState.find(u => u.usecase === AIR_FUNNEL_JOURNEY_TYPE)
    const mapped = item ? getUnifiedWidgetItemMappedForRender(item) : null
    const content = mapped?.usecase === AIR_FUNNEL_JOURNEY_TYPE ? mapped.content : []
    const hasContent = hasContentItems(content)

    if (!hasContent) {
        return null
    }

    return (
        <Container className="w-100p">
            <RecentSearchWidgetWrapper
                pageName={FHP_PAGE_NAME}
                vertical="air"
                isPwa={false}
                data={{ usecase: AIR_FUNNEL_JOURNEY_TYPE, content }}
                setData={update => updateUnifiedWidgetState(update as IUnifiedWidgetStateUpdate)}
                abVariant={abVariant}
            />
        </Container>
    )
})

type UnifiedWidgetStackProps = {
    onLoginClick: () => void
    onArrowClick?: () => void
    abVariant?: string
    showRecentSearch?: boolean
    showEliteBanner?: boolean
    onDataReady?: (height?: number) => void
    onDataRemoved?: () => void
}

const renderUnifiedWidgetItem = (
    item: IUnifiedWidgetItem,
    params: Pick<UnifiedWidgetStackProps, 'onLoginClick' | 'onArrowClick' | 'abVariant' | 'showRecentSearch' | 'showEliteBanner'>
): ReactNode => {
    const mappedItem = getUnifiedWidgetItemMappedForRender(item)
    if (!mappedItem) {
        return null
    }

    switch (mappedItem.usecase) {
        case AIR_FUNNEL_JOURNEY_TYPE: {
            if (!params.showRecentSearch) {
                return null
            }
            return <UnifiedRecentSearchSlot abVariant={params.abVariant} />
        }
        case AIR_LOYALTY_USECASE: {
            if (!params.showEliteBanner) {
                return null
            }
            return <UnifiedLoyaltyBannerSlot onLoginClick={params.onLoginClick} onArrowClick={params.onArrowClick} />
        }
        default:
            return null
    }
}

export const UnifiedWidgetStack = ({
    onLoginClick,
    onArrowClick,
    abVariant,
    showRecentSearch,
    showEliteBanner,
    onDataReady,
    onDataRemoved
}: UnifiedWidgetStackProps) => {
    const { unifiedState, loyalty, isSignedIn } = useUnifiedWidgetStack()
    const lastNotifiedHeightRef = useRef<number | null>(null)

    const loyaltyVariant = loyalty
        ? deriveBannerVariant(loyalty.metaData?.loyaltyStatus, isSignedIn, loyalty.metaData?.displayStatus)
        : null

    const combinedHeight = useMemo(
        () =>
            combinedHeightFromUnifiedState(unifiedState, {
                includeCys: Boolean(showRecentSearch),
                includeLoyalty: Boolean(showEliteBanner && loyaltyVariant)
            }),
        [unifiedState, showRecentSearch, showEliteBanner, loyaltyVariant]
    )

    useEffect(() => {
        if (combinedHeight <= 0) {
            if (lastNotifiedHeightRef.current !== null) {
                lastNotifiedHeightRef.current = null
                onDataRemoved?.()
            }
            return
        }
        if (lastNotifiedHeightRef.current !== combinedHeight) {
            lastNotifiedHeightRef.current = combinedHeight
            onDataReady?.(combinedHeight)
        }
    }, [combinedHeight, onDataReady, onDataRemoved])

    if (combinedHeight <= 0) {
        return null
    }

    return (
        <Container
            className="w-100p flex flex-column"
            css={{ paddingTop: WIDGET_MARGIN_HEIGHT, gap: WIDGET_STACK_GAP_PX }}
        >
            {unifiedState.map(item => (
                <Fragment key={item?.usecase}>
                    {renderUnifiedWidgetItem(item, {
                        onLoginClick,
                        onArrowClick,
                        abVariant,
                        showRecentSearch,
                        showEliteBanner
                    })}
                </Fragment>
            ))}
        </Container>
    )
}
